// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { DeviceCode, GroupSecurity } from '@quromedical/fhir-common';
import { CreateDeviceRequest, DeviceGetAllParams } from '@quromedical/models';
import { compact, flatten } from '@quromedical/utils';
import { useLinkTo } from '@react-navigation/native';
import { Buffer } from 'buffer';
import { CrudForm, SubmissionHandler } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  columns,
  DeviceTable,
  getDataFromResponse,
  initialSearch,
  searchFields,
} from 'core/device';
import { FormMedia, initialData, schema, createFields } from 'core/media';
import { PaginatedTable } from 'core/table';
import { FileKeyProgressMap, Section, ToggleCard } from 'design-system';
import { FetcherOptions } from 'hooks/usePaginatedData';
import { useResponsive } from 'hooks/useResponsive';
import { useRevalidation } from 'hooks/useRevalidation';
import { useToggle } from 'hooks/useToggle';
import { DeviceApi } from 'integration/aggregate';
import { DeviceStackScreenProps } from 'navigation';
import React, { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

import { CreateDeviceForm } from './DeviceCreateScreen';

const api = new DeviceApi();

export const DeviceListScreen: React.FC<DeviceStackScreenProps<'List'>> = () => {
  const isStriped = useResponsive({ md: true }, false);

  const [search, setSearch] = useState<DeviceGetAllParams>(initialSearch);
  const [isSecondaryButtonPressed, setSecondaryButtonPressed] = useToggle(false);
  const [progress, setProgress] = useState<FileKeyProgressMap>({});

  const [uploadsComplete, setUploadsComplete] = useState<boolean>(true);

  const revalidate = useRevalidation('Device');

  const navigate = useLinkTo();

  const fetcher = (options: FetcherOptions) =>
    api.getDevices({
      'device-name': search['device-name'] || undefined,
      type: search.type,
      identifier: search.type,
      pageToken: options.nextToken,
      ...options,
    });

  const onRowPress = useCallback(
    (row: Partial<DeviceTable>) =>
      navigate({
        screen: 'Admin',
        params: {
          screen: 'Device',
          params: { screen: 'View', params: { id: row.id as string } },
        },
      }),
    [navigate]
  );

  const getDeviceNames = (csv: string) => {
    const lines = csv.split('\n');
    const patchNames = lines.map(line => {
      const name = line.split(';')[0].trim();
      if (name === '') {
        return;
      }
      return name;
    });
    return compact(patchNames);
  };

  const handleSubmit = useCallback<SubmissionHandler<FormMedia>>(
    async data => {
      const fileData = data.media.map(csv => Buffer.from(csv.base64, 'base64').toString());

      const bulkRequest = flatten(fileData.map(getDeviceNames));

      const devices: CreateDeviceRequest[] = bulkRequest.map(device => {
        return { name: device, status: 'active', type: DeviceCode.patch };
      });

      await api.createBulkDevices({ devices });
      await revalidate();

      return {};
    },
    [revalidate]
  );

  const onFilesChange = useCallback(
    (data: FormMedia) => {
      data.media.map(file => {
        if (!progress[file.key]) {
          setProgress({
            ...progress,
            [file.key]: {
              ...file,
              status: 'done',
              total: 1,
              value: 1,
            },
          });
        }
      });
      setUploadsComplete(false);
    },
    [progress]
  );

  const fields = createFields(progress, uploadsComplete, ['text/csv']);

  return (
    <ScrollView>
      <WithGroup groups={[GroupSecurity.DeviceCreate]}>
        <Section unsetZIndex>
          <ToggleCard
            icon="phone-iphone"
            title={strings.DeviceToggleCardTitle}
            textExpand={strings.DeviceToggleCardExpandButtonText}
            textCollapse={strings.DeviceToggleCardCollapseButtonText}
            contentWrapperProps={{ paddingHorizontal: 0, paddingVertical: 0 }}
            showSecondaryButton
            secondaryButtonText={strings.DeviceToggleCardExpandSecondaryButtonText}
            setSecondaryButtonPressed={setSecondaryButtonPressed}
            isSecondaryButtonPressed={isSecondaryButtonPressed}
          >
            {isSecondaryButtonPressed ? (
              <CrudForm
                onSubmit={handleSubmit}
                fields={fields}
                initialValues={initialData}
                validationSchema={schema}
                buttonText="Submit"
                onChange={onFilesChange}
              />
            ) : (
              <CreateDeviceForm />
            )}
          </ToggleCard>
        </Section>
      </WithGroup>
      <Section unsetZIndex>
        <CrudForm
          fields={searchFields}
          initialValues={initialSearch}
          showSubmitButton={false}
          onChange={setSearch}
        />
      </Section>
      <Section>
        <PaginatedTable
          cacheKey={`Device${JSON.stringify(search)}`}
          fetcher={fetcher}
          columns={columns}
          showHeader={true}
          isStriped={isStriped}
          getDataFromResponse={getDataFromResponse}
          onRowPress={onRowPress}
        />
      </Section>
    </ScrollView>
  );
};
